import { z } from 'zod';
export const ticketingAddressSchema = z.looseObject({
    street: z.string().nullable().optional(),
    postcode: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    countryCode: z.string().nullable().optional(),
    company: z.string().nullable().optional(),
});
export const ticketingCardCustomerSchema = z.looseObject({
    '@id': z.string(),
    '@type': z.literal('Customer'),
    birthday: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    defaultAddress: ticketingAddressSchema.nullable().optional(),
});
export const ticketingLienSchema = z.looseObject({
    ayantDroit: z.string().nullable().optional(),
    aCharge: z.boolean().nullable().optional(),
    validite: z.string().nullable().optional(),
    validiteDetail: z.string().nullable().optional(),
});
export const ticketingCardUserSchema = z.looseObject({
    '@id': z.string(),
    '@type': z.enum(['ShopUser', 'Administrator']),
    email: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    userType: z.enum(['shop', 'admin']),
    // Absent on some cards in preprod, and the rest of the client already reads it defensively
    // (`card.user?.customer?.['@id']`) — the schema was the only place claiming it is always there.
    customer: ticketingCardCustomerSchema.nullable().optional(),
    dateOfBirth: z.string().nullable().optional(),
    genre: z.enum(['man', 'woman', 'other']).nullable().optional(),
    emailPersonnel: z.string().nullable().optional(),
    statutMarital: z.string().nullable().optional(),
    lastLogin: z.string().nullable().optional(),
    enabled: z.boolean().nullable().optional(),
    liens: z.array(ticketingLienSchema).nullable().optional(),
});
export const ticketingSiteSchema = z.looseObject({
    '@id': z.string(),
    '@type': z.literal('Site'),
    id: z.number(),
    name: z.string(),
});
const statutProfessionnelSchema = z
    .enum(['CDI', 'CDD', 'Retraité', 'Expatrié', 'Apprenti', 'Doctorant', 'Salarié sorti'])
    .nullable()
    .optional();
export const ticketingCardSchema = z.looseObject({
    '@id': z.string(),
    '@type': z.literal('Card'),
    id: z.number(),
    code: z.string(),
    user: ticketingCardUserSchema.nullable().optional(),
    matricule: z.string().nullable(),
    emailProfessionnel: z.string().nullable().optional(),
    telephoneProfessionnel: z.string().nullable().optional(),
    indiceDeReference: z.number().nullable().optional(),
    salarieStatut: z.enum(['salarie', 'stagiaire']).nullable().optional(),
    statutProfessionnel: statutProfessionnelSchema,
    service: z.string().nullable().optional(),
    site: ticketingSiteSchema.nullable().optional(),
    dateDernierContrat: z.string().nullable().optional(),
    dateEntree: z.string().nullable().optional(),
    dateSortie: z.string().nullable().optional(),
    dateDesactivation: z.string().nullable().optional(),
    subventionsDisabledAt: z.string().nullable().optional(),
    preferenceEmail: z.enum(['pro', 'perso']).nullable().optional(),
    commiteeId: z.number().optional(),
});
export const ticketingJustificatifSchema = z.looseObject({
    id: z.number(),
    type: z.string().nullable(),
    path: z.string().nullable(),
});
/** The only two relation types the billetterie accepts on write. */
export const lienTypeEnum = z.enum(['Conjoint', 'Enfant']);
export const ayantDroitStatutEnum = z.enum([
    'En attente',
    'Accepté',
    'Rejeté',
    'Document invalide',
]);
export const ticketingAyantDroitSchema = z.looseObject({
    '@id': z.string(),
    '@type': z.literal('AyantDroit'),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    dateOfBirth: z.string().nullable(),
    lienType: z.string(),
    statut: ayantDroitStatutEnum,
    justificatifs: z.array(ticketingJustificatifSchema).optional().default([]),
});
export const ticketingTaxonSchema = z.looseObject({
    '@id': z.string(),
    '@type': z.literal('Taxon'),
    id: z.number(),
    code: z.string(),
    parent: z.string().nullable(),
    children: z.array(z.string()),
    enabled: z.boolean(),
    translations: z.object({
        fr_FR: z.object({
            name: z.string(),
        }),
    }),
});
export const updateCardPayloadSchema = z.looseObject({
    service: z.string().nullable().optional(),
    emailProfessionnel: z.string().nullable().optional(),
    telephoneProfessionnel: z.string().nullable().optional(),
    indiceDeReference: z.string().nullable().optional(),
    salarieStatut: z.enum(['salarie', 'stagiaire']).nullable().optional(),
    statutProfessionnel: statutProfessionnelSchema,
    fonctionCSE: z.string().nullable().optional(),
    site: z.string().nullable().optional(),
    dateDernierContrat: z.string().nullable().optional(),
    dateEntree: z.string().nullable().optional(),
    dateSortie: z.string().nullable().optional(),
    matricule: z.string().nullable().optional(),
    preferenceEmail: z.enum(['pro', 'perso']).nullable().optional(),
});
export const updateCustomerPayloadSchema = z.looseObject({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phoneNumber: z.string().nullable().optional(),
    birthday: z.string().nullable().optional(),
    gender: z.enum(['u', 'm', 'f']).optional(),
});
